import { useEffect, useRef, useState } from 'react';
import { Hint, HintHighlight, hintHighlight } from '../hints';

interface Props {
  hints: Hint[];
  /** Cells to spotlight for the currently focused hint (or null to clear). */
  onHighlight: (h: HintHighlight | null) => void;
  /** Fired the first time the player expands or applies a hint (used for the leaderboard tag). */
  onUse: () => void;
  /** Mark the hint's suggested cells on the board (X out eliminations, or place the queen). */
  onApply: (h: Hint) => void;
}

export function HintsPanel({ hints, onHighlight, onUse, onApply }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);

  // Track the cursor so we can tell which card sits under a *stationary* pointer. Touch taps also
  // emit pointer events, but a finger has no resting position — forget it so hover stays mouse-only.
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      pointer.current = e.pointerType === 'mouse' ? { x: e.clientX, y: e.clientY } : null;
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  // Hints recompute on every board change (e.g. right after Apply). The new list re-renders
  // under a still-stationary cursor, so no fresh mouseenter fires. Re-derive the hovered card
  // from the pointer's position so rapid-fire Apply keeps previewing whatever slid under it.
  useEffect(() => {
    setExpanded(null);
    const p = pointer.current;
    if (!p) { setHovered(null); return; }
    const card = document.elementFromPoint(p.x, p.y)?.closest('.hint-card') as HTMLElement | null;
    const idx = card?.dataset.index;
    setHovered(idx != null ? Number(idx) : null);
  }, [hints]);

  const focus = expanded ?? hovered;
  useEffect(() => {
    if (focus === null || !hints[focus]) {
      onHighlight(null);
    } else {
      onHighlight(hintHighlight(hints[focus]));
    }
  }, [focus, hints]);

  function toggle(i: number) {
    setExpanded(prev => {
      const next = prev === i ? null : i;
      if (next !== null) onUse();
      return next;
    });
  }

  function apply(e: React.MouseEvent, h: Hint) {
    e.stopPropagation(); // don't toggle the card's expansion
    onUse();
    onApply(h);
  }

  return (
    <div className="hints-panel">
      <div className="hints-title">Hints</div>
      {hints.length === 0 ? (
        <p className="hints-empty">No hints right now — try marking Xs around your queens.</p>
      ) : (
        hints.map((h, i) => (
          <div
            key={`${h.rule}-${i}`}
            data-index={i}
            className={`hint-card${expanded === i ? ' hint-card-open' : ''}${h.kind === 'mistake' ? ' hint-card-mistake' : ''}`}
            onPointerEnter={e => { if (e.pointerType === 'mouse') setHovered(i); }}
            onPointerLeave={() => setHovered(prev => (prev === i ? null : prev))}
            onClick={() => toggle(i)}
          >
            <div className="hint-card-head">
              <span className="hint-name">{h.title}</span>
              {h.kind === 'eliminate' && <span className="hint-impact">eliminates {h.count}</span>}
              {h.kind === 'place' && <span className="hint-impact">place a queen</span>}
              {h.kind !== 'mistake' && (
                <button className="hint-apply" onClick={e => apply(e, h)}>Apply</button>
              )}
              <span className="hint-caret">{expanded === i ? '▾' : '▸'}</span>
            </div>
            {expanded === i && <p className="hint-explanation">{h.explanation}</p>}
          </div>
        ))
      )}
    </div>
  );
}
